import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { ApolloClient, InMemoryCache, ApolloProvider, from } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { createHttpLink } from "@apollo/client/link/http";
import { SITE_CONFIG } from "./config";

const EXTENSION_URL_PREFIXES = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
];

const isExtensionUrl = (value: unknown) =>
  typeof value === "string" &&
  EXTENSION_URL_PREFIXES.some((prefix) => value.startsWith(prefix));

const getErrorText = (value: unknown) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const maybeMessage = (value as { message?: unknown }).message;
    const maybeStack = (value as { stack?: unknown }).stack;
    return [maybeMessage, maybeStack]
      .map((part) => (typeof part === "string" ? part : ""))
      .filter(Boolean)
      .join(" ");
  }
  return String(value);
};

const shouldIgnoreExtensionError = (
  message: unknown,
  filename: unknown,
  error: unknown
) => {
  if (isExtensionUrl(filename)) return true;
  const text = `${getErrorText(message)} ${getErrorText(error)}`.toLowerCase();
  return text.includes("chrome-extension://") || text.includes("moz-extension://");
};

// Ignore browser-extension runtime errors that are injected outside our app code.
window.addEventListener("error", (event) => {
  if (!shouldIgnoreExtensionError(event.message, event.filename, event.error)) {
    return;
  }
  event.preventDefault();
});

window.addEventListener("unhandledrejection", (event) => {
  const reasonText = getErrorText(event.reason).toLowerCase();
  if (
    !reasonText.includes("chrome-extension://") &&
    !reasonText.includes("moz-extension://")
  ) {
    return;
  }
  event.preventDefault();
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    );
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    // Don't block the app if GraphQL server is down
  }
});

// HTTP link
const httpLink = createHttpLink({
  uri: SITE_CONFIG.graphqlUrl,
  credentials: 'include',
});

const client = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <ApolloProvider client={client}>
    <App />
  </ApolloProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
